import { css, type TemplateResult, nothing } from 'lit';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '../../library/editors/index.js';
import '../../library/editors/components/d-edit-classification.js';
import '../../library/elements/d-section.js';
import '../../library/fields/index.js';
import '../../library/fields/d-view-info.js';
import '../../library/lists/d-list-section-attachment.js';
import '../../library/lists/d-list-section-tutorial-participants.js';
import '../../library/lists/d-list-section.js';
import { EntityContent } from '../entity-content.js';
import type { ListItemIcon } from '../../library/lists/listItemIcon';
import type { UpdateSectionItem } from '../update-section-item';
import type { TutorialViewEditItem } from './tutorial-view-edit-item';
import type { TutorialView } from './tutorial-view';
import type { SectionField } from '../../library/components/section-field';
import { LocalDate } from 'src/utilities/local-date';
import { t } from 'src/i18n';

/**
 *
 */
@customElement('d-tutorial-view')
export class DTutorialView extends EntityContent<TutorialView, TutorialViewEditItem> {
  static readonly styles = [
    ...EntityContent.styles,
    css`
      d-list-section-tutorial-participants {
        margin-bottom: 12px;
      }

      .button.secondary {
        background-color: hsl(0, 0%, 95%);
        color: hsl(0, 0%, 20%);
      }

      .button.secondary:hover {
        background-color: hsl(0, 0%, 90%);
      }
    `,
  ];
  @property({ type: String })
  entityType = 'tutorials';
  @property({ type: Boolean })
  helpOpened = false;
  @property({ type: String })
  employeeId = '';
  @property({ type: String })
  today = LocalDate.now().toString();

  renderViewMode() {
    return html`${this.renderFieldSection('tutorials', this.viewFields(), this.entityView)}`;
  }

  hideEdit(): boolean {
    return true;
  }

  hideDelete(): boolean {
    return true;
  }

  hidePdf(): boolean {
    return true;
  }

  async initializeEditItem() {
    this.editItem = {
      name: this.entityView.name,
    };
  }

  _updateTutorial() {
    // Always create a new guide registration
    this.dispatchEvent(
      new CustomEvent<{ value?: string }>('update-tutorial', {
        bubbles: true,
        composed: true,
        detail: { value: this.entityView.uuid },
      }),
    );
  }

  _openExistingGuide(participantId: string) {
    this.dispatchEvent(
      new CustomEvent<{ participantId: string }>('open-guide-registration', {
        bubbles: true,
        composed: true,
        detail: { participantId },
      }),
    );
  }

  private get registrations() {
    return this.entityView.registrationsNew.concat(this.entityView.registrationsOld);
  }

  _openLatestIncompleteGuide() {
    // Find latest incomplete (not cancelled) guide and open it
    if (!this.registrations || this.registrations.length === 0) {
      return;
    }

    const sortedRegistrations = [...this.registrations].sort(
      (a, b) => new Date(b.registrationDate).getTime() - new Date(a.registrationDate).getTime(),
    );

    // Latest incomplete registration is the first one that is PENDING or IN_PROGRESS
    const latestIncomplete = sortedRegistrations.find(
      (r) => r.participants[0]?.status === 'PENDING' || r.participants[0]?.status === 'IN_PROGRESS',
    );

    if (latestIncomplete) {
      this._openExistingGuide(latestIncomplete.participants[0].id);
    }
  }

  hasIncompleteGuide(): boolean {
    if (!this.registrations || this.registrations.length === 0) {
      return false;
    }
    // Only check the LATEST registration (older ones are considered cancelled)
    const sortedRegistrations = [...this.registrations].sort(
      (a, b) => new Date(b.registrationDate).getTime() - new Date(a.registrationDate).getTime(),
    );
    const latestRegistration = sortedRegistrations[0];

    return (
      latestRegistration.participants[0]?.status === 'PENDING' ||
      latestRegistration.participants[0]?.status === 'IN_PROGRESS'
    );
  }

  hasRegistrations(): boolean {
    return !!(this.registrations && this.registrations.length > 0);
  }

  private get info() {
    if (this.entityView.guide) {
      return t('tutorials.guideInfo');
    }
    return t('tutorials.courseInfo');
  }

  renderGuideRegistrations() {
    const items = this.guideRegistrationsList();
    if (items.length === 0) {
      return nothing;
    }

    return html` <d-list-section no-header .items=${items}></d-list-section> `;
  }

  renderCourseRegistrations(currentVersion: boolean) {
    const registrations = currentVersion ? this.entityView.registrationsNew : this.entityView.registrationsOld;
    let versionLabel = '';
    if (currentVersion && this.entityView.registrationsOld.length > 0) {
      versionLabel = t('tutorials.onNewestCourseVersion');
    }
    if (!currentVersion) {
      versionLabel = t('tutorials.onPreviousCourseVersions');
    }
    return html`
      <d-list-section-tutorial-participants
        .today=${this.today}
        .currentVersion=${currentVersion}
        .versionLabel=${versionLabel}
        .tutorialId=${parseInt(this.entityView.uuid, 10)}
        .contentVersionId=${this.entityView.contentVersionId}
        .tutorialName=${this.entityView.name}
        .employeeId=${this.employeeId}
        .token=${this.token}
        .writeAccess=${this.entityView.currentUserHasWriteAccess}
        .employees=${this.entityView.employees}
        .registrations=${registrations}
        .opened=${currentVersion}
      ></d-list-section-tutorial-participants>
    `;
  }

  renderAllCourseRegistrations() {
    return html`
      ${this.renderCourseRegistrations(true)}
      ${this.entityView.registrationsOld.length ? html` ${this.renderCourseRegistrations(false)}` : nothing}
    `;
  }

  renderPreContent(): TemplateResult<1> {
    return html`
      <d-section>
        <d-view-info .content=${this.info}></d-view-info>
      </d-section>
      ${this.entityView.guide
        ? html`
            <d-section>
              <div style="display: flex; gap: 12px;">
                ${this.hasIncompleteGuide()
                  ? html`
                      <div class="button" @click=${() => this._openLatestIncompleteGuide()}>
                        ${t('tutorials.openCurrentGuide')}
                      </div>
                      <div class="button secondary" @click=${() => this._updateTutorial()}>
                        ${t('tutorials.restartGuide')}
                      </div>
                    `
                  : html`
                      <div class="button" @click=${() => this._updateTutorial()}>
                        ${this.hasRegistrations() ? t('tutorials.restartGuide') : t('tutorials.startGuide')}
                      </div>
                    `}
              </div>
            </d-section>
            ${this.renderGuideRegistrations()}
          `
        : html` ${this.renderAllCourseRegistrations()} `}
    `;
  }

  protected viewFields(): SectionField[] {
    return [
      {
        field: 'content',
        fieldForLabel: 'description',
        sublabel: this.entityView.registrationsOld.length > 0 ? t('tutorials.newestVersion') : '',
        type: 'view-html',
      },
    ];
  }

  protected asUpdateSectionItem(): UpdateSectionItem | undefined {
    return undefined;
  }

  protected renderAttachments(): TemplateResult<1> {
    return html``;
  }

  private guideRegistrationsList() {
    if (!this.registrations || this.registrations.length === 0) {
      return [];
    }

    const sortedRegistrations = [...this.registrations].sort(
      (a, b) => new Date(b.registrationDate).getTime() - new Date(a.registrationDate).getTime(),
    );

    return sortedRegistrations.map((registration, index) => {
      const participant = registration.participants[0];
      const registrationDate = new Date(registration.registrationDate);
      const day = registrationDate.getDate().toString().padStart(2, '0');
      const month = (registrationDate.getMonth() + 1).toString().padStart(2, '0');
      const year = registrationDate.getFullYear();
      const formattedDate = `${day}.${month}.${year}`;

      const isLatest = index === 0;

      let sublabel: string;
      let mainIcon: ListItemIcon | undefined = undefined;

      if (participant.status === 'COMPLETED') {
        // Completed registrations always show as completed with date
        if (participant.completed) {
          const completedDate = new Date(participant.completed);
          const completedDay = completedDate.getDate().toString().padStart(2, '0');
          const completedMonth = (completedDate.getMonth() + 1).toString().padStart(2, '0');
          const completedYear = completedDate.getFullYear();
          sublabel = t('tutorials.completedOnDate', { date: `${completedDay}.${completedMonth}.${completedYear}` });
        } else {
          sublabel = t('tutorials.completed');
        }
        mainIcon = 'checkBlue';
      } else if (isLatest) {
        // Latest incomplete registration - show current status
        if (participant.status === 'IN_PROGRESS') {
          sublabel = t('tutorials.inProgress');
        } else {
          sublabel = t('tutorials.waitingForCompletion');
        }
      } else {
        // Older incomplete registration - mark as cancelled
        sublabel = t('tutorials.cancelled');
      }

      return {
        label: t('tutorials.startedOnDate', { date: formattedDate }),
        accessible: false,
        secondaryLabel: sublabel,
        mainIcon,
      };
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-tutorial-view': DTutorialView;
  }
}
